import { Badge, Button, Flex, Modal, Typography } from "antd";
import {
  ModalHeader,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import { PopupScheduleBlock } from "components/WeeklyCalendar/components/Event";
import { CalendarSchedule } from "components/WeeklyCalendar/components/types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { actionCountLabel, actionCountWord } from "shared/utils";
import { colors } from "shared/theme/color";
import { tzDate } from "shared/utils/dayjs";
import { useScheduleStateOptional } from "../filter/ScheduleFilterContext";
import { textPx } from "shared/constants/zoom";

/**
 * Shared control rendered in both the calendar (column header) and the list
 * (date-group header): a chip-button that opens a modal listing the schedules
 * whose bookings still need actioning, each row navigating to the detail page.
 */

type ActionedSchedulesProps = {
  date: string;
  schedules: CalendarSchedule[];
};

export const ActionedSchedules: React.FC<ActionedSchedulesProps> = ({
  date,
  schedules,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isCreateDrawerOpen =
    useScheduleStateOptional()?.isCreateDrawerOpen ?? false;
  const count = schedules.length;

  // Auto-close the modal if the create drawer opens while it's visible.
  useEffect(() => {
    if (isCreateDrawerOpen) setOpen(false);
  }, [isCreateDrawerOpen]);

  // Nothing to action -> render nothing (no dead, disabled-grey button).
  if (count === 0) return null;

  // While the create drawer is open, collapse to a small amber dot.
  if (isCreateDrawerOpen) return <Badge color={colors.amber} />;

  return (
    <>
      <Button
        type="text"
        onClick={() => setOpen(!open)}
        // The full label rides along as a native tooltip because the calendar
        // hides the word in narrow day columns (index.css .calendar-day-head)
        // and shows only the amber count.
        title={actionCountLabel(count)}
        style={{
          height: "auto",
          // Sized so "1 Action" (the widest label) clears a centred day
          // number in a 150px calendar column, the width of a 1440px screen
          // with the sidebar collapsed. See .calendar-day-head in index.css.
          padding: "4px 8px",
          borderRadius: 999,
          background: colors.amberTint,
          color: colors.amberDark,
          fontSize: textPx(11),
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {/* One child on purpose: antd 5 buttons are inline-flex with an 8px
            gap between children, so count and word as siblings sat a full
            gap apart instead of one space. */}
        <span>
          {count}
          <span className="actioned-chip__label">
            {" "}
            {actionCountWord(count)}
          </span>
        </span>
      </Button>
      <Modal
        title={
          <ModalHeader
            eyebrow={date}
            title={translate("schedule.actionable_bookings.title")}
          />
        }
        open={open}
        onCancel={() => setOpen(!open)}
        footer={null}
        width={440}
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
          },
        }}
      >
        <Typography.Text style={modalSubtitleStyle}>
          {translate("schedule.actionable_bookings.subtitle")}
        </Typography.Text>
        <Flex gap={9} vertical style={{ marginTop: 16 }}>
          {schedules.map((schedule: CalendarSchedule, index: number) => (
            <PopupScheduleBlock
              key={index}
              event={schedule}
              onClick={() =>
                navigate(
                  `${PATHS.SCHEDULE}/${
                    schedule.schedule_id
                  }?occurrence_timestamp=${encodeURIComponent(
                    tzDate(schedule.start_time).toISOString()
                  )}`
                )
              }
            />
          ))}
        </Flex>
      </Modal>
    </>
  );
};
